import { useEffect, useMemo, useRef, useState } from "react";
import type { ProjectManifest } from "@pix/shared";
import {
  BUILTIN_SE,
  BUILTIN_SE_BY_PATH,
  KENNEY_SE_CATEGORY_LABEL,
  KENNEY_SE_CATEGORY_ORDER,
  PIX_STUDIO_SE_CATEGORY_ORDER,
  builtinSeCategoryLabel,
  isBuiltinSePath,
  type BuiltinSeEntry,
} from "@pix/builtin-se";
import { cacheListByProject, type AssetRecord } from "../storage/assetStore";

export type SePickSelection =
  | { source: "project"; file: string }
  | { source: "builtin"; builtin: BuiltinSeEntry };

export interface SePickResult {
  kind: "play" | "stop";
  selection?: SePickSelection;
  slot: number;
  vol?: number;
  loop?: boolean;
}

interface Props {
  manifest: ProjectManifest;
  projectId: string;
  /** 組み込み SE のプレビュー再生に使う engine の URL */
  engineUrl: string;
  initial: {
    kind?: "play" | "stop";
    file?: string;
    slot?: number;
    vol?: number;
    loop?: boolean;
  } | null;
  onSelect: (result: SePickResult) => void;
  onCancel: () => void;
}

const MAX_SLOT = 15;
type Source = "builtin" | "project";
type PackGroup = "kenney" | "pix-studio";

const KENNEY_CATS = KENNEY_SE_CATEGORY_ORDER.map((id) => ({
  id,
  label: KENNEY_SE_CATEGORY_LABEL[id] ?? id,
}));

/** entry.path を URL 安全な absolute URL に変換 */
function builtinUrl(engineUrl: string, path: string): string {
  const base = engineUrl.replace(/\/$/, "");
  const encoded = path
    .split("/")
    .map((seg) => encodeURIComponent(seg))
    .join("/");
  return `${base}/${encoded}`;
}

export function SePickerModal({
  manifest,
  projectId,
  engineUrl,
  initial,
  onSelect,
  onCancel,
}: Props) {
  const seEntries = useMemo(
    () => Object.entries(manifest.audio?.se ?? {}),
    [manifest.audio?.se],
  );

  const projectSeEntries = useMemo(
    () => seEntries.filter(([, def]) => !isBuiltinSePath(def.file)),
    [seEntries],
  );

  // 既存セルから初期値推定
  const initialBuiltin: BuiltinSeEntry | null = useMemo(() => {
    if (initial?.file) {
      const def = manifest.audio?.se?.[initial.file];
      if (def && isBuiltinSePath(def.file)) {
        return BUILTIN_SE_BY_PATH[def.file] ?? null;
      }
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialSource: Source = initialBuiltin
    ? "builtin"
    : projectSeEntries.length === 0
      ? "builtin"
      : "project";

  const initialPackGroup: PackGroup =
    initialBuiltin?.pack === "pix-studio" ? "pix-studio" : "kenney";

  const initialCategory: string =
    initialBuiltin?.category ??
    (initialPackGroup === "kenney" ? "click" : PIX_STUDIO_SE_CATEGORY_ORDER[0]?.id ?? "");

  // --- state ---
  const [mode, setMode] = useState<"play" | "stop">(initial?.kind ?? "play");
  const [source, setSource] = useState<Source>(initialSource);
  const [slot, setSlot] = useState<number>(initial?.slot ?? 0);
  const [vol, setVol] = useState<number>(initial?.vol ?? 100);
  const [loop, setLoop] = useState<boolean>(initial?.loop ?? false);

  const [packGroup, setPackGroup] = useState<PackGroup>(initialPackGroup);
  const [activeCategory, setActiveCategory] = useState<string>(initialCategory);
  const [search, setSearch] = useState<string>("");
  const [showAdult, setShowAdult] = useState<boolean>(false);

  const [selectedBuiltin, setSelectedBuiltin] = useState<BuiltinSeEntry | null>(
    initialBuiltin,
  );
  const [selectedProject, setSelectedProject] = useState<string | null>(
    initial?.file && !initialBuiltin ? initial.file : null,
  );

  // --- preview audio ---
  const [projectUrlMap, setProjectUrlMap] = useState<Map<string, string>>(new Map());
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingKey, setPlayingKey] = useState<string | null>(null);

  useEffect(() => {
    let canceled = false;
    cacheListByProject(projectId).then((list: AssetRecord[]) => {
      if (canceled) return;
      const m = new Map<string, string>();
      for (const a of list) {
        if (a.mime.startsWith("audio/")) m.set(a.path, URL.createObjectURL(a.blob));
      }
      setProjectUrlMap(m);
    });
    return () => {
      canceled = true;
    };
  }, [projectId]);

  useEffect(() => {
    return () => {
      for (const url of projectUrlMap.values()) URL.revokeObjectURL(url);
      const a = audioRef.current;
      if (a) {
        a.pause();
        audioRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const stopPreview = () => {
    const a = audioRef.current;
    if (a) {
      a.pause();
      audioRef.current = null;
    }
    setPlayingKey(null);
  };

  const playUrl = (key: string, url: string) => {
    stopPreview();
    const a = new Audio(url);
    a.volume = Math.max(0, Math.min(1, vol / 100));
    a.loop = false;
    a.addEventListener("ended", () => {
      if (audioRef.current === a) {
        audioRef.current = null;
        setPlayingKey(null);
      }
    });
    audioRef.current = a;
    setPlayingKey(key);
    void a.play().catch(() => {
      audioRef.current = null;
      setPlayingKey(null);
    });
  };

  const previewBuiltin = (entry: BuiltinSeEntry) => {
    if (playingKey === entry.id) stopPreview();
    else playUrl(entry.id, builtinUrl(engineUrl, entry.path));
  };
  const previewProject = (id: string) => {
    const def = manifest.audio?.se?.[id];
    if (!def) return;
    const url = projectUrlMap.get(def.file);
    if (!url) {
      console.warn(`SE preview: blob not cached for ${def.file}`);
      return;
    }
    const key = `project:${id}`;
    if (playingKey === key) stopPreview();
    else playUrl(key, url);
  };

  // --- 組み込み の絞り込み ---
  const isPackMatch = (entry: BuiltinSeEntry): boolean =>
    packGroup === "kenney" ? entry.pack !== "pix-studio" : entry.pack === "pix-studio";

  const categories: { id: string; label: string }[] =
    packGroup === "kenney" ? KENNEY_CATS : PIX_STUDIO_SE_CATEGORY_ORDER;

  const isSearching = search.trim() !== "";
  const filteredBuiltin: BuiltinSeEntry[] = useMemo(() => {
    const q = search.trim().toLowerCase();
    let pool = BUILTIN_SE.filter(isPackMatch);
    if (!showAdult) pool = pool.filter((e) => !e.adult);
    if (q !== "") {
      return pool.filter(
        (e) =>
          e.id.toLowerCase().includes(q) ||
          e.name.toLowerCase().includes(q) ||
          e.description.toLowerCase().includes(q) ||
          e.tags.some((t) => t.toLowerCase().includes(q)),
      );
    }
    return pool.filter((e) => e.category === activeCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, activeCategory, showAdult, packGroup]);

  // pack 切替時に有効な category がなければ先頭にフォールバック
  useEffect(() => {
    if (!categories.find((c) => c.id === activeCategory)) {
      setActiveCategory(categories[0]?.id ?? "");
    }
  }, [packGroup, categories, activeCategory]);

  // --- 確定 ---
  const onConfirmPlay = () => {
    stopPreview();
    if (source === "builtin") {
      if (!selectedBuiltin) return;
      onSelect({
        kind: "play",
        selection: { source: "builtin", builtin: selectedBuiltin },
        slot,
        ...(vol !== 100 ? { vol } : {}),
        ...(loop ? { loop: true } : {}),
      });
    } else {
      if (!selectedProject) return;
      onSelect({
        kind: "play",
        selection: { source: "project", file: selectedProject },
        slot,
        ...(vol !== 100 ? { vol } : {}),
        ...(loop ? { loop: true } : {}),
      });
    }
  };
  const onConfirmStop = () => {
    stopPreview();
    onSelect({ kind: "stop", slot });
  };
  const canConfirm =
    mode === "stop" ||
    (source === "builtin" ? selectedBuiltin !== null : selectedProject !== null);

  return (
    <div className="modal-backdrop" onMouseDown={onCancel}>
      <div className="modal se-picker-modal" onMouseDown={(e) => e.stopPropagation()}>
        <h2>
          効果音 <span className="modal-sub">{BUILTIN_SE.length} 個の組み込み SE</span>
        </h2>

        <div className="se-picker-tabs">
          <button
            className={"se-picker-tab" + (mode === "play" ? " active" : "")}
            onClick={() => setMode("play")}
          >
            ▶ 再生
          </button>
          <button
            className={"se-picker-tab" + (mode === "stop" ? " active" : "")}
            onClick={() => setMode("stop")}
          >
            ■ 停止
          </button>
        </div>

        {mode === "play" && (
          <>
            <div className="se-picker-source-tabs">
              <button
                className={
                  "se-picker-source-tab" + (source === "builtin" ? " active" : "")
                }
                onClick={() => setSource("builtin")}
              >
                組み込み
              </button>
              <button
                className={
                  "se-picker-source-tab" + (source === "project" ? " active" : "")
                }
                onClick={() => setSource("project")}
              >
                プロジェクト ({projectSeEntries.length})
              </button>
            </div>

            {source === "builtin" && (
              <>
                <div className="se-picker-pack-tabs">
                  <button
                    className={
                      "se-picker-pack-tab" +
                      (packGroup === "kenney" ? " active" : "")
                    }
                    onClick={() => setPackGroup("kenney")}
                  >
                    Kenney CC0 (354)
                  </button>
                  <button
                    className={
                      "se-picker-pack-tab" +
                      (packGroup === "pix-studio" ? " active" : "")
                    }
                    onClick={() => setPackGroup("pix-studio")}
                  >
                    PIX STUDIO (1472)
                  </button>
                </div>
                <div className="se-picker-controls">
                  <input
                    type="search"
                    className="se-picker-search"
                    placeholder="検索(名前 / タグ / 説明)"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <label className="se-picker-adult-toggle">
                    <input
                      type="checkbox"
                      checked={showAdult}
                      onChange={(e) => setShowAdult(e.target.checked)}
                    />
                    <span>成人向け表示</span>
                  </label>
                </div>
                {!isSearching && (
                  <div className="se-picker-cat-tabs">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        className={
                          "se-picker-cat-tab" +
                          (activeCategory === cat.id ? " active" : "")
                        }
                        onClick={() => setActiveCategory(cat.id)}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                )}
                <BuiltinList
                  entries={filteredBuiltin}
                  selected={selectedBuiltin}
                  onSelect={setSelectedBuiltin}
                  playingKey={playingKey}
                  onPreview={previewBuiltin}
                  isSearching={isSearching}
                />
              </>
            )}

            {source === "project" && (
              <ProjectPane
                entries={projectSeEntries}
                urlMap={projectUrlMap}
                selected={selectedProject}
                onSelect={setSelectedProject}
                playingKey={playingKey}
                onPreview={previewProject}
              />
            )}
          </>
        )}

        {mode === "stop" && (
          <div className="se-picker-stop-desc">
            指定スロットで再生中の効果音を停止します。
            <br />
            <small>
              slot=0..{MAX_SLOT} を選択 / 全停止は各スロットで停止コマンドを実行してください。
            </small>
          </div>
        )}

        <div className="se-picker-opts">
          <label className="se-picker-opt">
            <span>slot</span>
            <input
              type="number"
              min={0}
              max={MAX_SLOT}
              value={slot}
              onChange={(e) =>
                setSlot(Math.max(0, Math.min(MAX_SLOT, Number(e.target.value))))
              }
            />
          </label>
          {mode === "play" && (
            <>
              <label className="se-picker-opt">
                <span>vol {vol}%</span>
                <input
                  type="range"
                  min={0}
                  max={200}
                  value={vol}
                  onChange={(e) => setVol(Number(e.target.value))}
                />
              </label>
              <label className="se-picker-opt se-picker-opt-loop">
                <input
                  type="checkbox"
                  checked={loop}
                  onChange={(e) => setLoop(e.target.checked)}
                />
                <span>ループ再生</span>
              </label>
            </>
          )}
        </div>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onCancel}>
            キャンセル
          </button>
          {mode === "play" ? (
            <button
              className="btn btn-primary"
              disabled={!canConfirm}
              onClick={onConfirmPlay}
            >
              この SE を挿入
            </button>
          ) : (
            <button className="btn btn-primary" onClick={onConfirmStop}>
              停止コマンドを挿入
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// =================================================================
function BuiltinList({
  entries,
  selected,
  onSelect,
  playingKey,
  onPreview,
  isSearching,
}: {
  entries: BuiltinSeEntry[];
  selected: BuiltinSeEntry | null;
  onSelect: (e: BuiltinSeEntry) => void;
  playingKey: string | null;
  onPreview: (e: BuiltinSeEntry) => void;
  isSearching: boolean;
}) {
  return (
    <div className="picker-list se-picker-list">
      {entries.length === 0 ? (
        <div className="picker-empty">
          {isSearching ? "一致する SE がありません。" : "このカテゴリに SE はありません。"}
        </div>
      ) : (
        entries.map((entry) => {
          const isPlaying = playingKey === entry.id;
          const isSelected = selected?.id === entry.id;
          const catLabel = builtinSeCategoryLabel(entry);
          return (
            <div
              key={entry.id}
              className={"se-picker-row" + (isSelected ? " active" : "")}
            >
              <button
                className="se-picker-preview"
                title="プレビュー再生"
                onClick={(e) => {
                  e.stopPropagation();
                  onPreview(entry);
                }}
              >
                {isPlaying ? "■" : "▶"}
              </button>
              <button
                className="se-picker-pick"
                onClick={() => onSelect(entry)}
                title="この SE を選択"
              >
                <div className="se-picker-id">
                  {entry.name}
                  {entry.adult && <span className="se-picker-adult-badge">18+</span>}
                </div>
                <div className="se-picker-path">
                  {catLabel}
                  {entry.description && (
                    <span className="se-picker-desc"> › {entry.description}</span>
                  )}
                </div>
              </button>
            </div>
          );
        })
      )}
    </div>
  );
}

// =================================================================
function ProjectPane({
  entries,
  urlMap,
  selected,
  onSelect,
  playingKey,
  onPreview,
}: {
  entries: [string, { file: string }][];
  urlMap: Map<string, string>;
  selected: string | null;
  onSelect: (id: string) => void;
  playingKey: string | null;
  onPreview: (id: string) => void;
}) {
  if (entries.length === 0) {
    return (
      <div className="picker-empty">
        manifest にユーザー定義 SE はありません。
        <br />
        AssetPanel から音声ファイルをアップロードすると、ここに表示されます。
        <br />
        組み込み SE はそのまま使えます。
      </div>
    );
  }
  return (
    <div className="picker-list se-picker-list">
      {entries.map(([id, def]) => {
        const url = urlMap.get(def.file);
        const key = `project:${id}`;
        const isPlaying = playingKey === key;
        const isSelected = selected === id;
        return (
          <div
            key={id}
            className={"se-picker-row" + (isSelected ? " active" : "")}
          >
            <button
              className="se-picker-preview"
              title={url ? "プレビュー再生" : "(blob 未キャッシュ)"}
              disabled={!url}
              onClick={(e) => {
                e.stopPropagation();
                onPreview(id);
              }}
            >
              {isPlaying ? "■" : "▶"}
            </button>
            <button
              className="se-picker-pick"
              onClick={() => onSelect(id)}
              title="この SE を選択"
            >
              <div className="se-picker-id">{id}</div>
              <div className="se-picker-path">{def.file}</div>
            </button>
          </div>
        );
      })}
    </div>
  );
}
